import proj4 from "proj4";

// Constants for error messages
const ERROR_MESSAGES = {
  NO_FILE_SELECTED: "NO_FILE_SELECTED",
  MULTIPLE_FILES: "MULTIPLE_FILES",
  FILE_TOO_LARGE: "FILE_TOO_LARGE",
  INVALID_FORMAT: "INVALID_FORMAT",
  INVALID_GEOJSON: "INVALID_GEOJSON",
  INVALID_GEOMETRY_TYPE: "INVALID_GEOMETRY_TYPE",
  INVALID_COORDINATE_SYSTEM: "INVALID_COORDINATE_SYSTEM",
  PARSE_ERROR: "PARSE_ERROR",
};

// Define coordinate systems
proj4.defs("EPSG:4326", "+proj=longlat +datum=WGS84 +no_defs");
proj4.defs(
  "EPSG:2039",
  "+proj=tmerc +lat_0=31.73439361111111 +lon_0=35.20451694444445 +k=1.0000067 +x_0=219529.584 +y_0=626907.39 +ellps=GRS80 +towgs84=-24.0024,-17.1032,-17.8444,-0.33077,-1.85269,1.66969,5.4248 +units=m +no_defs"
);

class PolygonUploadUtils {
  // Validate file selection
  static validateFileSelection(files) {
    if (!files || files.length === 0) {
      return { isValid: false, error: ERROR_MESSAGES.NO_FILE_SELECTED };
    }

    if (files.length > 1) {
      return { isValid: false, error: ERROR_MESSAGES.MULTIPLE_FILES };
    }

    return { isValid: true, error: null };
  }

  // Validate file size (9.5MB = 9.5 * 1024 * 1024 bytes)
  static validateFileSize(file) {
    const maxSize = 9.5 * 1024 * 1024; // 9.5MB in bytes

    if (file.size > maxSize) {
      return { isValid: false, error: ERROR_MESSAGES.FILE_TOO_LARGE };
    }

    return { isValid: true, error: null };
  }

  // Validate file format
  static validateFileFormat(file) {
    const validExtensions = [".geojson", ".json"];
    const fileName = file.name.toLowerCase();
    const hasValidExtension = validExtensions.some((ext) => fileName.endsWith(ext));

    if (!hasValidExtension) {
      return { isValid: false, error: ERROR_MESSAGES.INVALID_FORMAT };
    }

    return { isValid: true, error: null };
  }

  // Parse and validate GeoJSON content
  static async validateGeoJSONContent(file) {
    try {
      const text = await file.text();
      const geojson = JSON.parse(text);

      // Check if it's a valid Polygon or MultiPolygon geometry
      if (!geojson.type || !geojson.coordinates) {
        return { isValid: false, error: ERROR_MESSAGES.INVALID_GEOJSON };
      }

      // Check if it's a valid geometry type
      const validGeometryTypes = ["Polygon", "MultiPolygon"];
      if (!validGeometryTypes.includes(geojson.type)) {
        return { isValid: false, error: ERROR_MESSAGES.INVALID_GEOMETRY_TYPE };
      }

      // Check coordinate system
      const coordinateSystem = this.detectCoordinateSystemFromGeometry(geojson);

      if (!coordinateSystem) {
        return { isValid: false, error: ERROR_MESSAGES.INVALID_COORDINATE_SYSTEM };
      }

      return {
        isValid: true,
        error: null,
        geojson,
        coordinateSystem,
      };
    } catch (error) {
      console.error("Error in validateGeoJSONContent:", error);
      return { isValid: false, error: ERROR_MESSAGES.PARSE_ERROR };
    }
  }

  // Detect coordinate system from GeoJSON coordinates
  static detectCoordinateSystem(geojson) {
    try {
      // Get first coordinate from first feature
      const firstFeature = geojson.features[0];

      if (!firstFeature || !firstFeature.geometry || !firstFeature.geometry.coordinates) {
        return null;
      }

      let firstCoord;

      if (firstFeature.geometry.type === "Polygon") {
        firstCoord = firstFeature.geometry.coordinates[0][0];
      } else if (firstFeature.geometry.type === "MultiPolygon") {
        firstCoord = firstFeature.geometry.coordinates[0][0][0];
      } else {
        return null;
      }

      if (!firstCoord || firstCoord.length < 2) {
        return null;
      }

      const [x, y] = firstCoord;

      // Check if it's EPSG:4326 (WGS84) - longitude between -180 and 180, latitude between -90 and 90
      if (x >= -180 && x <= 180 && y >= -90 && y <= 90) {
        return "EPSG:4326";
      }

      // Check if it's EPSG:2039 (Israel TM Grid) - typical ranges for Israel
      // X coordinates typically between 100000 and 300000, Y between 500000 and 800000
      if (x >= 100000 && x <= 300000 && y >= 500000 && y <= 800000) {
        return "EPSG:2039";
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  // Detect coordinate system from geometry object (Polygon or MultiPolygon)
  static detectCoordinateSystemFromGeometry(geometry) {
    try {
      if (!geometry.coordinates) {
        return null;
      }

      let firstCoord;

      if (geometry.type === "Polygon") {
        firstCoord = geometry.coordinates[0][0];
      } else if (geometry.type === "MultiPolygon") {
        firstCoord = geometry.coordinates[0][0][0];
      } else {
        return null;
      }

      if (!firstCoord || firstCoord.length < 2) {
        return null;
      }

      const [x, y] = firstCoord;

      // Check if it's EPSG:4326 (WGS84) - longitude between -180 and 180, latitude between -90 and 90
      if (x >= -180 && x <= 180 && y >= -90 && y <= 90) {
        return "EPSG:4326";
      }

      // Check if it's EPSG:2039 (Israel TM Grid) - typical ranges for Israel
      // X coordinates typically between 100000 and 300000, Y between 500000 and 800000
      if (x >= 100000 && x <= 300000 && y >= 500000 && y <= 800000) {
        return "EPSG:2039";
      }

      return null;
    } catch (error) {
      console.error("Error in detectCoordinateSystemFromGeometry:", error);
      return null;
    }
  }

  // Convert Polygon to MultiPolygon
  static convertPolygonToMultiPolygon(geometry) {
    if (geometry.type === "Polygon") {
      return {
        type: "MultiPolygon",
        coordinates: [geometry.coordinates],
      };
    }
    return geometry;
  }

  // Transform coordinates from EPSG:2039 to EPSG:4326
  static transformCoordinates(geojson, fromCRS) {
    if (fromCRS === "EPSG:4326") {
      return geojson; // No transformation needed
    }

    if (fromCRS !== "EPSG:2039") {
      throw new Error("Unsupported coordinate system");
    }

    const transformedFeatures = geojson.features.map((feature) => {
      const transformedGeometry = this.transformGeometry(feature.geometry, fromCRS);
      return {
        ...feature,
        geometry: transformedGeometry,
      };
    });

    return {
      ...geojson,
      features: transformedFeatures,
    };
  }

  // Transform geometry coordinates
  static transformGeometry(geometry, fromCRS) {
    if (geometry.type === "Polygon") {
      const transformedCoordinates = geometry.coordinates.map((ring) => {
        return ring.map((coord) => {
          const [x, y] = coord;
          const [lon, lat] = proj4(fromCRS, "EPSG:4326", [x, y]);
          return [lon, lat];
        });
      });

      return {
        type: "Polygon",
        coordinates: transformedCoordinates,
      };
    }

    if (geometry.type === "MultiPolygon") {
      const transformedCoordinates = geometry.coordinates.map((polygon) => {
        return polygon.map((ring) => {
          return ring.map((coord) => {
            const [x, y] = coord;
            const [lon, lat] = proj4(fromCRS, "EPSG:4326", [x, y]);
            return [lon, lat];
          });
        });
      });

      return {
        type: "MultiPolygon",
        coordinates: transformedCoordinates,
      };
    }

    return geometry;
  }

  // Main validation and processing function
  static async processPolygonFile(file) {
    // Step 1: Validate file selection
    const selectionValidation = this.validateFileSelection([file]);

    if (!selectionValidation.isValid) {
      return { success: false, error: selectionValidation.error };
    }

    // Step 2: Validate file size
    const sizeValidation = this.validateFileSize(file);

    if (!sizeValidation.isValid) {
      return { success: false, error: sizeValidation.error };
    }

    // Step 3: Validate file format
    const formatValidation = this.validateFileFormat(file);

    if (!formatValidation.isValid) {
      return { success: false, error: formatValidation.error };
    }

    // Step 4: Parse and validate GeoJSON content
    const contentValidation = await this.validateGeoJSONContent(file);

    if (!contentValidation.isValid) {
      return { success: false, error: contentValidation.error };
    }

    let processedGeojson = contentValidation.geojson;

    // Step 5: Convert Polygon to MultiPolygon if needed
    if (processedGeojson.type === "Polygon") {
      processedGeojson = this.convertPolygonToMultiPolygon(processedGeojson);
    }

    // Step 6: Transform coordinates if needed
    if (contentValidation.coordinateSystem === "EPSG:2039") {
      processedGeojson = this.transformGeometry(processedGeojson, "EPSG:2039");
    }

    return {
      success: true,
      geojson: processedGeojson,
      originalCoordinateSystem: contentValidation.coordinateSystem,
    };
  }
}

export { ERROR_MESSAGES };
export default PolygonUploadUtils;
